import React, { useEffect, useState } from "react";
import api, { todayISO } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, Empty } from "@/components/common";
import { toast } from "sonner";
import { CheckCircle2, Sparkles, Pencil } from "lucide-react";

const NumRow = ({ label, value, onChange, max = 10, plus = false }) => (
  <div className="mb-5">
    <label className="text-sm text-slate-200 font-medium">{label}</label>
    <div className="flex flex-wrap gap-1.5 mt-2">
      {Array.from({ length: max + 1 }, (_, n) => n).map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)} data-testid={`pick-${label.replace(/[^a-z]/gi, "").toLowerCase()}-${n}`}
          className={`w-10 h-10 rounded-lg text-sm font-semibold transition-all ${value === n ? "bg-[#C9A24B] text-[#0b1120] scale-105" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>
          {plus && n === max ? `${n}+` : n}
        </button>
      ))}
    </div>
  </div>
);

export default function SimpleTracker() {
  const { user } = useApp();
  const date = todayISO();
  const [loading, setLoading] = useState(true);
  const [tech, setTech] = useState(null);
  const [detected, setDetected] = useState(0);
  const [existing, setExisting] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ procedures_assisted: 0, reviews: 0, testimonials: 0, training_status: "None", training_topic: "", has_issue: false, issue_note: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const load = () => {
    setLoading(true);
    api.get("/trackers/today", { params: { date } }).then(({ data }) => {
      setTech(data.technician);
      setDetected(data.detected_procedures);
      setExisting(data.tracker);
      if (data.tracker) {
        const t = data.tracker;
        setF({ procedures_assisted: t.procedures_assisted ?? data.detected_procedures ?? 0, reviews: t.reviews || 0, testimonials: t.testimonials || 0, training_status: t.training_status || "None", training_topic: t.training_topic || "", has_issue: !!t.has_issue, issue_note: t.issue_note || "" });
        setEditing(false);
      } else {
        setF((p) => ({ ...p, procedures_assisted: data.detected_procedures || 0 }));
        setEditing(true);
      }
    }).catch(() => toast.error("Could not load today's tracker")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async () => {
    if (!tech) { toast.error("No technician linked to your account"); return; }
    setSaving(true);
    // Merge onto any existing detailed record so supervisor/auto fields are preserved
    const base = existing || {};
    const payload = { ...base, date, technician_id: tech.employee_id, branch: tech.branch,
      procedures_assisted: f.procedures_assisted, reviews: f.reviews, testimonials: f.testimonials,
      training_status: f.training_status, training_topic: f.training_topic,
      has_issue: f.has_issue, issue_note: f.has_issue ? f.issue_note : "" };
    delete payload.id; delete payload.updated_at;
    try {
      await api.post("/trackers", payload);
      toast.success("Today submitted — thank you!");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <Empty text="Loading today…" />;
  if (!tech) return (
    <div className="max-w-xl mx-auto"><Card className="p-8 text-center"><p className="text-slate-300">Your Google account isn't linked to a technician profile yet. Please ask the OT Head to add you in the Master List.</p></Card></div>
  );

  return (
    <div className="animate-fade-up max-w-xl mx-auto" data-testid="simple-tracker-page">
      <div className="mb-6">
        <p className="text-[#C9A24B] text-sm">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
        <h1 className="text-3xl font-heading font-light text-white mt-1">Hello, {tech.name.split(" ")[0]} 👋</h1>
        <p className="text-slate-400 text-sm mt-1">{tech.role} · {tech.branch} · {tech.employee_id}</p>
      </div>

      {!editing && existing ? (
        <Card className="p-6" data-testid="tracker-submitted-status">
          <div className="flex items-center gap-2 text-emerald-400 font-semibold mb-4"><CheckCircle2 size={20} /> TODAY SUBMITTED</div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-400">Procedures</span><span className="font-semibold text-white">{f.procedures_assisted}</span></div>
            <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-400">Reviews</span><span className="font-semibold text-white">{f.reviews}</span></div>
            <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-400">Testimonials</span><span className="font-semibold text-white">{f.testimonials}</span></div>
            <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-400">Training</span><span className="font-semibold text-white">{f.training_status}</span></div>
            <div className="flex justify-between border-b border-white/5 pb-2 col-span-2"><span className="text-slate-400">Issue</span><span className={`font-semibold ${f.has_issue ? "text-[#DC2626]" : "text-emerald-400"}`}>{f.has_issue ? `Yes — ${f.issue_note || "reported"}` : "No"}</span></div>
          </div>
          <button data-testid="edit-today-btn" onClick={() => setEditing(true)} className="mt-5 flex items-center gap-2 text-[#C9A24B] text-sm hover:brightness-125"><Pencil size={15} /> Edit today's entry</button>
        </Card>
      ) : (
        <Card className="p-6">
          {detected > 0 ? (
            <div className="mb-5 bg-emerald-600/10 border border-emerald-600/30 rounded-lg p-4">
              <div className="flex items-center gap-2 text-emerald-400 text-sm font-semibold mb-1"><Sparkles size={16} /> Auto-detected from Surgery Log</div>
              <p className="text-slate-300 text-sm">Procedures today: <span className="font-bold text-white">{detected}</span></p>
              <div className="flex gap-2 mt-2">
                <button onClick={() => set("procedures_assisted", detected)} data-testid="confirm-detected-btn" className={`text-xs px-3 py-1.5 rounded-md ${f.procedures_assisted === detected ? "bg-emerald-600 text-white" : "bg-white/10 text-slate-300"}`}>✓ Correct ({detected})</button>
                <span className="text-xs text-slate-500 self-center">or adjust below</span>
              </div>
            </div>
          ) : null}

          <NumRow label="Procedures Assisted Today" value={f.procedures_assisted} onChange={(v) => set("procedures_assisted", v)} max={10} plus />
          <NumRow label="Reviews Collected" value={f.reviews} onChange={(v) => set("reviews", v)} max={20} />
          <NumRow label="Testimonial Videos" value={f.testimonials} onChange={(v) => set("testimonials", v)} max={20} />

          <div className="mb-5">
            <label className="text-sm text-slate-200 font-medium">Training Today</label>
            <div className="flex gap-2 mt-2">
              {["None", "Completed"].map((o) => (
                <button key={o} data-testid={`training-${o}`} onClick={() => set("training_status", o)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${f.training_status === o ? "bg-[#C9A24B] text-[#0b1120]" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>{o}</button>
              ))}
            </div>
            {f.training_status === "Completed" && (
              <input data-testid="training-topic" value={f.training_topic} onChange={(e) => set("training_topic", e.target.value)} placeholder="Topic (optional)"
                className="mt-3 w-full bg-[#0f172a] border border-white/10 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#C9A24B] focus:outline-none" />
            )}
          </div>

          <div className="mb-5">
            <label className="text-sm text-slate-200 font-medium">Any Issue?</label>
            <div className="flex gap-2 mt-2">
              {[["No", false], ["Yes", true]].map(([lbl, val]) => (
                <button key={lbl} data-testid={`issue-${lbl}`} onClick={() => set("has_issue", val)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${f.has_issue === val ? (val ? "bg-[#DC2626] text-white" : "bg-emerald-600 text-white") : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>{lbl}</button>
              ))}
            </div>
            {f.has_issue && (
              <input data-testid="issue-note" value={f.issue_note} onChange={(e) => set("issue_note", e.target.value)} placeholder="What happened? (short note)"
                className="mt-3 w-full bg-[#0f172a] border border-white/10 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#C9A24B] focus:outline-none" />
            )}
          </div>

          <button data-testid="submit-today-btn" onClick={submit} disabled={saving}
            className="w-full bg-emerald-600 text-white font-semibold py-3 rounded-lg hover:brightness-110 transition-all disabled:opacity-50 text-base">
            {saving ? "Saving…" : "SUBMIT TODAY"}
          </button>
          <p className="text-center text-xs text-slate-600 mt-3">Everything else — KPI, incentive, reports — is calculated automatically.</p>
        </Card>
      )}
    </div>
  );
}
